import {
  buildBaseWorkflowStrip,
  type WorkflowPanelRoute,
  type WorkflowStripItem,
} from './integratedRepairStrip';
import {
  ClipboardCheck,
  Thermometer,
  Wrench,
  Eye,
  Scan,
  Layers,
  ListChecks,
} from 'lucide-react';
import { spoolTw } from './spoolUiPalette';

/** Steps in a full repair cycle (same order as production, numbered). */
export type RepairCycleStep =
  | 'fitup'
  | 'preheat'
  | 'welding'
  | 'vt'
  | 'ndt'
  | 'coating'
  | 'accept';

export type RepairCycleMeta = {
  repairNo: number;
  cycleStep: RepairCycleStep;
};

const REPAIR_CYCLE_STEP_ORDER: RepairCycleStep[] = [
  'fitup',
  'preheat',
  'welding',
  'vt',
  'ndt',
  'coating',
  'accept',
];

export function repairCycleStripKey(cycleStep: RepairCycleStep, repairNo: number): string {
  return `repair_${cycleStep}_${repairNo}`;
}

export function firstRepairCycleStripKey(repairNo: number): string {
  return repairCycleStripKey('fitup', repairNo);
}

export function parseRepairCycleStripKey(stripKey: string): RepairCycleMeta | null {
  const m = stripKey.match(/^repair_(fitup|preheat|welding|vt|ndt|coating|accept)_(\d+)$/);
  if (!m) return null;
  const repairNo = Number(m[2]);
  if (!Number.isFinite(repairNo)) return null;
  return { cycleStep: m[1] as RepairCycleStep, repairNo };
}

/** Legacy API / NCR strip keys (`repair_weld`, `vt_retest`, …). */
export function parseLegacyRepairStripKey(stripKey: string): { repairNo: number } | null {
  if (stripKey === 'repair_weld') return { repairNo: 1 };
  const w = stripKey.match(/^repair_weld_(\d+)$/);
  if (w) return { repairNo: Number(w[1]) };
  const r = stripKey.match(/^(?:vt_retest|ndt_retest)_(\d+)$/);
  if (r) return { repairNo: Number(r[1]) };
  return null;
}

function panelForCycleStep(step: RepairCycleStep): WorkflowPanelRoute {
  return step;
}

function stepMeta(step: RepairCycleStep, repairNo: number) {
  const n = String(repairNo);
  switch (step) {
    case 'fitup':
      return {
        short: `Fit-Up ${n}`,
        label: `Fit-Up ${n}`,
        Icon: ClipboardCheck,
        role: 'QC Inspector',
        roleColor: spoolTw.roleQcPill,
      };
    case 'preheat':
      return {
        short: `Preheat ${n}`,
        label: `Preheat ${n}`,
        Icon: Thermometer,
        role: 'Welder',
        roleColor: 'bg-blue-100 text-blue-700',
      };
    case 'welding':
      return {
        short: `Welding ${n}`,
        label: `Welding ${n}`,
        Icon: Wrench,
        role: 'Welder',
        roleColor: 'bg-blue-100 text-blue-700',
      };
    case 'vt':
      return {
        short: `VT ${n}`,
        label: `Visual (VT) ${n}`,
        Icon: Eye,
        role: 'QC Inspector',
        roleColor: spoolTw.roleQcPill,
      };
    case 'ndt':
      return {
        short: `NDT ${n}`,
        label: `NDT ${n}`,
        Icon: Scan,
        role: 'NDT Operator Lv II',
        roleColor: 'bg-orange-100 text-orange-700',
      };
    case 'coating':
      return {
        short: `Coating ${n}`,
        label: `Coating ${n}`,
        Icon: Layers,
        role: 'Coating Inspector',
        roleColor: 'bg-purple-100 text-purple-700',
      };
    case 'accept':
      return {
        short: `Accept ${n}`,
        label: `Accept ${n}`,
        Icon: ListChecks,
        role: 'QC Inspector',
        roleColor: 'bg-green-100 text-green-700',
      };
  }
}

/** All seven steps for one repair cycle — appended after production Accept (no in-between pills). */
export function buildFullRepairCyclePills(repairNo: number): WorkflowStripItem[] {
  return REPAIR_CYCLE_STEP_ORDER.map((cycleStep) => {
    const meta = stepMeta(cycleStep, repairNo);
    const panel = panelForCycleStep(cycleStep);
    const item: WorkflowStripItem = {
      stripKey: repairCycleStripKey(cycleStep, repairNo),
      panel,
      short: meta.short,
      label: meta.label,
      Icon: meta.Icon,
      role: meta.role,
      roleColor: meta.roleColor,
      repairCycle: { repairNo, cycleStep },
    };
    if (cycleStep === 'vt') item.vtPhase = 'standard';
    if (cycleStep === 'ndt') item.ndtPhase = 'standard';
    return item;
  });
}

/**
 * Production strip + full repair cycles appended after Accept.
 * Each cycle: Fit-Up n → Preheat n → Welding n → VT n → NDT n → Coating n → Accept n.
 */
export function buildWorkflowStripWithRepairCycles(repairNumbers: number[]): WorkflowStripItem[] {
  const base = buildBaseWorkflowStrip();
  if (!repairNumbers.length) return base;
  const repairPills = repairNumbers.flatMap((n) => buildFullRepairCyclePills(n));
  return [...base, ...repairPills];
}
